/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';

import { GeneralQueryParam } from '../../../ts';
import type {
  DiscoveryCollection,
  DiscoveryCredentials,
  DiscoveryEnvironment,
  DiscoveryField,
  DiscoveryFieldExamples,
  DiscoveryProject,
  GetDiscoveryInstancesResponse
} from '../../../ts/discovery';
import { LastOperation } from '../../../ts/resource-controller';
import { endpoints, isWxoEnabled } from '../../ia/util/config';

const DISCOVERY = {
  instances: '/discovery/instances'
};

export const getDiscoveryInstances =
  async (): Promise<GetDiscoveryInstancesResponse> => {
    const { data } = await axios.get<GetDiscoveryInstancesResponse>(
      `${endpoints.bluemix}${DISCOVERY.instances}`
    );
    return data;
  };

export const getDiscoveryInstanceCollections = async (
  instanceId: string,
  cfApi: string
): Promise<DiscoveryCollection[]> => {
  // In the WXO environment we can not send the instance CRN as a path
  // parameter since a proxy in that environment will spuriously decode it
  // and break the routing on the server. We will thus send it as a query
  // parameter, which will retain its encoding.
  const { data } = await axios.get<DiscoveryCollection[]>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/collections`,
    {
      params: { cfApi, [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const getDiscoveryV2InstanceProjectsCollections = async (
  instanceId: string
): Promise<DiscoveryProject[]> => {
  const { data } = await axios.get<DiscoveryProject[]>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/projects`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const getDiscoveryV2ProjectFields = async (
  projectId: string,
  instanceId: string
): Promise<DiscoveryField[]> => {
  const { data } = await axios.get<DiscoveryField[]>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/projects/${projectId}/fields`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const getDiscoveryCollectionFields = async (
  collectionId: string,
  instanceId: string,
  cfApi: string,
  environmentId: string,
  skillId: string
): Promise<DiscoveryField[]> => {
  const { data } = await axios.get<DiscoveryField[]>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/collections/${collectionId}/fields`,
    {
      params: {
        cfApi,
        environmentId,
        skillId,
        [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId
      }
    }
  );
  return data;
};

// FIXME This does not seem to have an equivalent in the server: is it used?
export const createDiscoveryInstance = async (instanceId: string) => {
  const { data } = await axios.post(
    `${endpoints.bluemix}${DISCOVERY.instances}`,
    { instanceId }
  );
  return data;
};

export const checkDiscoveryInstanceState = async (
  instanceId: string
): Promise<LastOperation> => {
  const { data } = await axios.get<LastOperation>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/state`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const createDiscoveryCredentials = async (
  instanceId: string
): Promise<DiscoveryCredentials> => {
  const { data } = await axios.post<DiscoveryCredentials>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/credentials`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

// FIXME This does not seem to have an equivalent in the server: is it used?
export const createDiscoveryEnvironment = async (instanceId: string) => {
  const { data } = await axios.post(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/environments`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const getDiscoveryEnvironmentInfo = async (
  environmentId: string,
  instanceId: string,
  cfApi: string
): Promise<DiscoveryEnvironment> => {
  const { data } = await axios.get<DiscoveryEnvironment>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/environments/${environmentId}`,
    {
      params: { cfApi, [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};

export const getDiscoveryCollectionDetail = async (
  environmentId: string,
  collectionId: string,
  instanceId: string,
  cfApi: string,
  skillId: string
): Promise<DiscoveryCollection> => {
  const { data } = await axios.get<DiscoveryCollection>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/environments/${environmentId}/collections/${collectionId}`,
    {
      params: {
        cfApi,
        skillId,
        [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId
      }
    }
  );
  return data;
};

export const getDiscoveryCollectionFieldExamples = async (
  instanceId: string,
  environmentId: string,
  collectionId: string,
  skillId: string,
  cfApi: string
): Promise<DiscoveryFieldExamples> => {
  const { data } = await axios.get<DiscoveryFieldExamples>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/collections/${collectionId}/fieldExamples`,
    {
      params: {
        cfApi,
        environmentId,
        skillId,
        [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId
      }
    }
  );
  return data;
};

export const getDiscoveryProjectFieldExamples = async (
  instanceId: string,
  projectId: string
): Promise<DiscoveryFieldExamples> => {
  const { data } = await axios.get<DiscoveryFieldExamples>(
    `${endpoints.bluemix}${DISCOVERY.instances}/${
      isWxoEnabled ? 'DISCOVERY_INSTANCE_CRN' : encodeURIComponent(instanceId)
    }/projects/${projectId}/fieldExamples`,
    {
      params: { [GeneralQueryParam.DISCOVERY_INSTANCE_CRN]: instanceId }
    }
  );
  return data;
};
